import api from '../utils/axios';

export const createTemplate = async (formData) => {
  try {
    const response = await api.post('/templates', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getAllTemplates = async (search, documentTypes = [], page = 1, limit = 12) => {
  try {
    const params = { page, limit };
    if (search) {
      params.search = search;
    }
    if (documentTypes && documentTypes.length > 0) {
      params.documentType = documentTypes.join(',');
    }
    const response = await api.get('/templates', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getTemplateById = async (id) => {
  try {
    const response = await api.get(`/templates/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const updateTemplate = async (id, data) => {
  try {
    const response = await api.put(`/templates/${id}`, data);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const deleteTemplate = async (id) => {
  try {
    const response = await api.delete(`/templates/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const suggestTemplate = async (formData) => {
  try {
    const response = await api.post('/templates/suggest', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const generateAITemplate = async (userIntent, options = {}) => {
  try {
    const response = await api.post('/templates/generate-ai', {
      userIntent,
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Chat with AI for document creation (POST /templates/ai-chat).
 * Sends full messages array; backend returns either:
 * - createdDocument: false → { success, message: { role: "assistant", content } }
 * - createdDocument: true  → { success, data: { document, generatedDocument, emailSubject, emailMessage }, message }
 */
export const chatWithAITemplate = async (messages) => {
  try {
    const response = await api.post('/templates/ai-chat', { messages });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Get AI conversation histories (GET /templates/ai-conversations).
 * @param {Object} params - { page, limit, status }
 * @returns {Promise<{ success, data, pagination }>}
 */
export const getAIConversations = async (params = {}) => {
  try {
    const { page = 1, limit = 10, status = 'completed' } = params;
    const response = await api.get('/templates/ai-conversations', {
      params: { page, limit, status },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Get a single AI conversation by ID (GET /templates/ai-conversations/:id).
 * @returns {Promise<{ success, data }>} data: { _id, title, messages, status, documentId, createdAt, updatedAt }
 */
export const getAIConversationById = async (id) => {
  try {
    const response = await api.get(`/templates/ai-conversations/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Check if the current company's organization has multiple companies.
 * Fetched from backend (not cached).
 */
export const getOrganizationHasMultipleCompanies = async () => {
  try {
    const response = await api.get("/templates/organization-has-multiple-companies");
    return response.data?.hasMultipleCompanies ?? false;
  } catch (error) {
    return false;
  }
};

/**
 * Get templates from other companies in the same organization.
 * Only available when org has multiple companies.
 */
export const getTemplatesFromOtherCompanies = async () => {
  try {
    const response = await api.get("/templates/from-other-companies");
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Clone a template from another company to the current company.
 * @param {string} templateId - ID of the template to clone
 * @returns {Promise<{ data }>} - The newly created template
 */
export const cloneTemplate = async (templateId) => {
  try {
    const response = await api.post(`/templates/clone/${templateId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
}; 